import type { ChatMessage } from '../services/supabaseChatService';

export interface AdminChatUser {
  id: string;
  fullName: string;
  avatar?: string;
  email?: string | null;
  unreadCount: number;
  lastMessage?: string;
  lastMessageTime?: string;
}

export interface AdminChatMessageEvent {
  sender_id: string;
  receiver_id: string;
  content?: string | null;
  created_at?: string | null;
}

type AdminChatMessageLike = Pick<
  ChatMessage,
  'sender_id' | 'receiver_id' | 'content' | 'created_at' | 'read_at'
>;

const getChatActivityTime = (value?: string | null) => new Date(value ?? 0).getTime();

export const compareAdminChatUsers = (a: AdminChatUser, b: AdminChatUser) => {
  const timestampDiff = getChatActivityTime(b.lastMessageTime) - getChatActivityTime(a.lastMessageTime);
  if (timestampDiff !== 0) return timestampDiff;

  const unreadDiff = (b.unreadCount ?? 0) - (a.unreadCount ?? 0);
  if (unreadDiff !== 0) return unreadDiff;

  return a.fullName.localeCompare(b.fullName);
};

export const sortAdminChatUsers = (users: AdminChatUser[]) => [...users].sort(compareAdminChatUsers);

export const applyMessageEventToAdminChatUsers = (
  users: AdminChatUser[],
  currentUserId: string,
  message: AdminChatMessageLike,
  previousMessage?: AdminChatMessageLike | null
) => {
  if (!currentUserId || users.length === 0) return users;
  if (message.sender_id !== currentUserId && message.receiver_id !== currentUserId) {
    return users;
  }

  const otherUserId =
    message.sender_id === currentUserId ? message.receiver_id : message.sender_id;

  if (!otherUserId) return users;

  const targetIndex = users.findIndex((user) => user.id === otherUserId);
  if (targetIndex === -1) return users;

  const currentUser = users[targetIndex];
  const wasUnreadForCurrentUser = Boolean(
    previousMessage &&
      previousMessage.sender_id === otherUserId &&
      previousMessage.receiver_id === currentUserId &&
      !previousMessage.read_at
  );
  const isUnreadForCurrentUser =
    message.sender_id === otherUserId &&
    message.receiver_id === currentUserId &&
    !message.read_at;

  let unreadCount = currentUser.unreadCount ?? 0;
  if (!previousMessage && isUnreadForCurrentUser) {
    unreadCount += 1;
  } else if (previousMessage && wasUnreadForCurrentUser !== isUnreadForCurrentUser) {
    unreadCount += isUnreadForCurrentUser ? 1 : -1;
  }

  const nextUnreadCount = Math.max(0, unreadCount);
  const currentTimestamp = getChatActivityTime(currentUser.lastMessageTime);
  const messageTimestamp = getChatActivityTime(message.created_at);
  const shouldPromoteMessage = messageTimestamp >= currentTimestamp && messageTimestamp > 0;

  const nextUser: AdminChatUser = {
    ...currentUser,
    unreadCount: nextUnreadCount,
    lastMessage: shouldPromoteMessage ? (message.content ?? currentUser.lastMessage) : currentUser.lastMessage,
    lastMessageTime: shouldPromoteMessage
      ? (message.created_at ?? currentUser.lastMessageTime)
      : currentUser.lastMessageTime,
  };

  const unchanged =
    nextUser.unreadCount === currentUser.unreadCount &&
    nextUser.lastMessage === currentUser.lastMessage &&
    nextUser.lastMessageTime === currentUser.lastMessageTime;

  if (unchanged) {
    return users;
  }

  const nextUsers = [...users];
  nextUsers[targetIndex] = nextUser;

  return sortAdminChatUsers(nextUsers);
};